import React, { useEffect, useMemo, useRef, useState } from "react";
import Globe from "react-globe.gl";
import { Lock, Anchor, Crown, Sparkles } from "lucide-react";

/**
 * TerritorySphere — 3D Boarding Deck globe (Director directive 2026.02).
 *
 * Replaces the 2D <USMap /> grid on the Boarding Deck with a high-fidelity
 * Three.js sphere that mirrors the Sovereign Eye visual language:
 *   • deep navy globe (earth-night)
 *   • gold atmosphere halo
 *   • Hawaii-anchored default focal point
 *   • each US state rendered as a clickable point at its centroid
 *
 * State coloring:
 *   • available  → pulsing gold (the prize)
 *   • warning    → muted amber (claimable but advanced)
 *   • locked     → deep red (already taken — visually present but unclickable)
 *   • selected   → bright cyan with ring (the locked-in choice)
 *
 * The sphere is the only path to selection — clicking a non-available state
 * surfaces a toast (handled by parent) instead of changing selection.
 */

// US state centroids — same map used by Sovereign Eye fleet endpoint.
const STATE_CENTROIDS = {
    AL: [32.806, -86.791], AK: [61.370, -152.404], AZ: [33.729, -111.431],
    AR: [34.969, -92.373], CA: [36.116, -119.681], CO: [39.059, -105.311],
    CT: [41.597, -72.755], DE: [39.318, -75.507], FL: [27.766, -81.687],
    GA: [33.040, -83.643], HI: [21.094, -157.498], ID: [44.240, -114.479],
    IL: [40.349, -88.986], IN: [39.849, -86.258], IA: [42.011, -93.210],
    KS: [38.526, -96.726], KY: [37.668, -84.670], LA: [31.169, -91.867],
    ME: [44.693, -69.381], MD: [39.063, -76.802], MA: [42.230, -71.530],
    MI: [43.326, -84.536], MN: [45.694, -93.900], MS: [32.741, -89.678],
    MO: [38.456, -92.288], MT: [46.921, -110.454], NE: [41.125, -98.268],
    NV: [38.313, -117.055], NH: [43.452, -71.563], NJ: [40.298, -74.521],
    NM: [34.840, -106.248], NY: [42.165, -74.948], NC: [35.630, -79.806],
    ND: [47.529, -99.784], OH: [40.388, -82.764], OK: [35.565, -96.928],
    OR: [44.572, -122.070], PA: [40.590, -77.209], RI: [41.680, -71.511],
    SC: [33.856, -80.945], SD: [44.299, -99.439], TN: [35.747, -86.692],
    TX: [31.054, -97.563], UT: [40.150, -111.862], VT: [44.045, -72.710],
    VA: [37.769, -78.169], WA: [47.401, -121.490], WV: [38.491, -80.954],
    WI: [44.268, -89.616], WY: [42.755, -107.302],
};

const HAWAII = { lat: 21.094, lng: -157.498, altitude: 1.8 };

const TIER_PRICE = { bronze: 1000, silver: 2500, gold: 5000 };

// State.status → visual meta. Director directive: gold = the prize.
function styleFor(status, isSelected) {
    if (isSelected) return { color: "#22D3EE", size: 0.95, ring: true, label: "SELECTED" };
    if (status === "available") return { color: "#F2C94C", size: 0.7, ring: true, label: "AVAILABLE" };
    if (status === "warning") return { color: "#FFAA22", size: 0.55, ring: false, label: "ADVANCED" };
    return { color: "#7A2A2A", size: 0.4, ring: false, label: "CLAIMED" };
}

export default function TerritorySphere({ territories, selectedCode, onSelect, height = 560 }) {
    const globeRef = useRef(null);
    const containerRef = useRef(null);
    const [width, setWidth] = useState(800);
    const [framed, setFramed] = useState(false);
    const [hovered, setHovered] = useState(null);

    // Responsive sizing
    useEffect(() => {
        const measure = () => {
            if (containerRef.current) setWidth(containerRef.current.clientWidth);
        };
        measure();
        window.addEventListener("resize", measure);
        return () => window.removeEventListener("resize", measure);
    }, []);

    // Default focus → Hawaii (Sovereign Anchor port).
    useEffect(() => {
        if (!globeRef.current || framed) return;
        try {
            globeRef.current.pointOfView(HAWAII, 1500);
            const controls = globeRef.current.controls();
            if (controls) {
                controls.autoRotate = false;        // user interaction first; no auto-rotate during selection
                controls.enableZoom = true;          // boarding requires zooming to find a state
                controls.minDistance = 200;
                controls.maxDistance = 600;
            }
            setFramed(true);
        } catch (e) {
            // Globe not ready yet
        }
    }, [framed]);

    // Each territory becomes a globe point. Hover lifts label + ring.
    const points = useMemo(() => {
        return (territories || [])
            .map((t) => {
                const sc = (t.state_code || "").toUpperCase();
                const c = STATE_CENTROIDS[sc];
                if (!c) return null;
                const meta = styleFor(t.status, sc === selectedCode);
                return {
                    state_code: sc,
                    state_name: t.state_name,
                    tier: t.tier,
                    status: t.status,
                    lat: c[0],
                    lng: c[1],
                    color: meta.color,
                    size: meta.size,
                    label: `${t.state_name} · ${meta.label}${t.tier ? ` · ${t.tier.toUpperCase()}` : ""}${t.tier ? ` · $${TIER_PRICE[t.tier]?.toLocaleString() || "—"}` : ""}`,
                    raw: t,
                };
            })
            .filter(Boolean);
    }, [territories, selectedCode]);

    // Available territories also emit a soft pulsing ring on the sphere.
    const rings = useMemo(() => {
        const r = points
            .filter((p) => p.status === "available")
            .map((p) => ({ lat: p.lat, lng: p.lng, maxR: 4, propagationSpeed: 1.2, repeatPeriod: 2400 }));
        // Hawaii anchor pulse (always on — home port marker).
        r.push({ lat: HAWAII.lat, lng: HAWAII.lng, maxR: 6, propagationSpeed: 1.6, repeatPeriod: 1800, anchor: true });
        // Selected state pulses cyan.
        const sel = points.find((p) => p.state_code === selectedCode);
        if (sel) {
            r.push({ lat: sel.lat, lng: sel.lng, maxR: 5, propagationSpeed: 1.4, repeatPeriod: 1500, selected: true });
        }
        return r;
    }, [points, selectedCode]);

    const handlePointClick = (point) => {
        if (!point || !onSelect) return;
        // Smoothly recentre on the clicked state for a "lock" effect.
        try {
            const c = STATE_CENTROIDS[point.state_code];
            if (c && globeRef.current) {
                globeRef.current.pointOfView({ lat: c[0], lng: c[1], altitude: 1.5 }, 1200);
            }
        } catch { /* noop */ }
        onSelect(point.raw);
    };

    const stats = useMemo(() => {
        const s = { available: 0, warning: 0, locked: 0 };
        points.forEach((p) => {
            if (p.status === "available") s.available++;
            else if (p.status === "warning") s.warning++;
            else s.locked++;
        });
        return s;
    }, [points]);

    return (
        <div
            ref={containerRef}
            data-testid="territory-sphere"
            className="relative w-full rounded-2xl overflow-hidden"
            style={{
                height,
                background: "radial-gradient(ellipse at center, hsl(220 50% 8%) 0%, hsl(220 60% 4%) 60%, #000 100%)",
            }}
        >
            <div
                aria-hidden="true"
                className="absolute inset-0 pointer-events-none"
                style={{ background: "radial-gradient(ellipse at 30% 30%, hsl(40 70% 45% / 0.10), transparent 60%)" }}
            />

            {/* Globe canvas */}
            <div className="absolute inset-0">
                <Globe
                    ref={globeRef}
                    width={width}
                    height={height}
                    backgroundColor="rgba(0,0,0,0)"
                    showAtmosphere
                    atmosphereColor="#F2C94C"
                    atmosphereAltitude={0.18}
                    globeImageUrl="//unpkg.com/three-globe/example/img/earth-night.jpg"
                    bumpImageUrl="//unpkg.com/three-globe/example/img/earth-topology.png"
                    // Territory points
                    pointsData={points}
                    pointColor="color"
                    pointAltitude={0.014}
                    pointRadius="size"
                    pointLabel="label"
                    onPointClick={handlePointClick}
                    onPointHover={setHovered}
                    // Pulsing rings on available states + Hawaii + selected
                    ringsData={rings}
                    ringColor={() => (t) => `rgba(242, 201, 76, ${1 - t})`}
                    ringMaxRadius="maxR"
                    ringPropagationSpeed="propagationSpeed"
                    ringRepeatPeriod="repeatPeriod"
                />
            </div>

            {/* Top-left HUD */}
            <div className="absolute top-4 left-4 right-4 flex items-start justify-between pointer-events-none z-10">
                <div className="glass rounded-lg px-3 py-2 pointer-events-auto" data-testid="sphere-title">
                    <div className="flex items-center gap-2">
                        <Crown size={14} className="text-[hsl(45_85%_68%)]" />
                        <span className="font-heading uppercase tracking-[0.3em] text-[10px] text-[hsl(45_85%_75%)]">
                            Territory Sphere
                        </span>
                    </div>
                    <div className="text-[9px] uppercase tracking-[0.25em] text-muted-foreground mt-1">
                        Click a gold pulse to lock
                    </div>
                </div>

                {/* Stats badge */}
                <div className="glass rounded-lg px-3 py-2 pointer-events-auto flex items-center gap-3" data-testid="sphere-stats">
                    <Stat color="#F2C94C" label="Available" value={stats.available} testid="sphere-available" />
                    <Stat color="#FFAA22" label="Advanced" value={stats.warning} testid="sphere-advanced" />
                    <Stat color="#7A2A2A" label="Claimed" value={stats.locked} testid="sphere-locked" />
                </div>
            </div>

            {/* Hovered tooltip */}
            {hovered && (
                <div
                    className="absolute bottom-16 left-4 glass rounded-lg px-4 py-3 pointer-events-none max-w-xs"
                    data-testid="sphere-hover-tooltip"
                >
                    <div className="flex items-center gap-2">
                        <span
                            className="w-2.5 h-2.5 rounded-full"
                            style={{ background: hovered.color, boxShadow: `0 0 8px ${hovered.color}` }}
                        />
                        <div className="font-heading text-base">{hovered.state_name}</div>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.25em] text-muted-foreground mt-1">
                        {hovered.status === "available" ? `Tier ${hovered.tier?.toUpperCase()} · $${TIER_PRICE[hovered.tier]?.toLocaleString() || "—"}` :
                         hovered.status === "warning" ? "Advanced · contact Director" :
                         "Already claimed"}
                    </div>
                    {hovered.status === "available" && (
                        <div className="mt-2 inline-flex items-center gap-1 text-[10px] text-[hsl(45_85%_75%)]">
                            <Sparkles size={10} /> Click to lock
                        </div>
                    )}
                </div>
            )}

            {/* Bottom-right anchor footer */}
            <div className="absolute bottom-3 right-4 text-[9px] uppercase tracking-[0.3em] text-muted-foreground pointer-events-none flex items-center gap-1.5">
                <Anchor size={10} /> Anchored · Honolulu, HI
            </div>

            {/* Selected lock badge */}
            {selectedCode && (
                <div
                    className="absolute bottom-4 left-4 glass rounded-lg px-4 py-3 pointer-events-none border-[hsl(180_85%_60%/0.5)]"
                    data-testid="sphere-locked-badge"
                    style={{ background: "hsl(180 50% 12% / 0.7)", borderWidth: "1.5px" }}
                >
                    <div className="flex items-center gap-2">
                        <Lock size={14} className="text-[hsl(180_85%_70%)]" />
                        <div>
                            <div className="text-[9px] uppercase tracking-[0.3em] text-[hsl(180_85%_75%)]">Locked</div>
                            <div className="font-heading text-lg leading-tight">
                                {points.find((p) => p.state_code === selectedCode)?.state_name || selectedCode}
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Stat({ color, label, value, testid }) {
    return (
        <div className="flex items-center gap-1.5" data-testid={testid}>
            <span className="w-2 h-2 rounded-full" style={{ background: color, boxShadow: `0 0 6px ${color}` }} />
            <span className="text-[9px] uppercase tracking-[0.2em] text-muted-foreground">{label}</span>
            <span className="font-mono text-xs text-foreground">{value}</span>
        </div>
    );
}
